import React from 'react';
import styled from 'styled-components';

import { Skeleton } from '@material-ui/lab';
import { Datum, ResponsiveLine } from '@nivo/line';

import { useAdminGetDailyActiveUsersQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDailyActiveUsersQuery';
import { toLocaleString } from '../../utils/format-number';
import { getValue, prettyPrintDate } from './dashboardUtils';

type Props = {
  fromDate: string | null;
  toDate: string | null;
};

type SparklineProps = {
  data: Datum[];
  color: string;
  valueSuffix?: string;
};

const Sparkline: React.FC<SparklineProps> = ({ data, color, valueSuffix = '' }) => {
  if (!data.length) {
    return (
      <ChartArea>
        <ChartPlaceholderText>No data</ChartPlaceholderText>
      </ChartArea>
    );
  }

  return (
    <ChartArea>
      <ResponsiveLine
        data={[{ id: 'series', data }]}
        margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        enableGridX={false}
        enableGridY={false}
        enablePoints={false}
        curve="monotoneX"
        colors={[color]}
        lineWidth={2}
        enableArea
        areaOpacity={0.15}
        useMesh
        enableSlices="x"
        sliceTooltip={({ slice }) => {
          const point = slice.points[0];
          if (!point) return null;
          return (
            <TooltipBox>
              <TooltipDate>{String(point.data.x)}</TooltipDate>
              <TooltipValue style={{ color }}>
                {String(point.data.yFormatted)}
                {valueSuffix}
              </TooltipValue>
            </TooltipBox>
          );
        }}
      />
    </ChartArea>
  );
};

type MetricCardProps = {
  title: string;
  description: string;
  value: string;
  isMissing?: boolean;
  chartData: Datum[];
  chartColor: string;
  chartSuffix?: string;
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  description,
  value,
  isMissing,
  chartData,
  chartColor,
  chartSuffix,
}) => (
  <Card>
    <CardHeader>
      <CardTitleBlock>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardTitleBlock>
      <CardValue $muted={isMissing}>{value}</CardValue>
    </CardHeader>
    <Sparkline data={chartData} color={chartColor} valueSuffix={chartSuffix} />
  </Card>
);

const toPercent = (numerator: number, denominator: number): number =>
  denominator === 0 ? 0 : Math.round((numerator / denominator) * 1000) / 10;

export const CoreAction: React.FC<Props> = ({ fromDate, toDate }) => {
  const { data, isFetching } = useAdminGetDailyActiveUsersQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
  });

  const items = data?.items || [];

  const {
    totalActive,
    totalCoreAction,
    totalActivated7d,
    totalActivated14d,
    coreActionSeries,
    coreActionPctSeries,
    timeToCoreSeries,
    repeatSeries,
  } = React.useMemo(() => {
    let totalActive = 0;
    let totalCoreAction = 0;
    let totalActivated7d = 0;
    let totalActivated14d = 0;
    const coreActionSeries: Datum[] = [];
    const coreActionPctSeries: Datum[] = [];
    const timeToCoreSeries: Datum[] = [];
    const repeatSeries: Datum[] = [];

    items.forEach((item) => {
      const active = getValue(item.totalActiveUsers);
      const core = getValue(item.usersWithCompleteReservations);
      const activated7 = getValue(item.usersActivated7DaysWithReservation);
      const activated14 = getValue(item.usersActivated14DaysWithTwoReservations);
      const x = prettyPrintDate(item.date);

      totalActive += active;
      totalCoreAction += core;
      totalActivated7d += activated7;
      totalActivated14d += activated14;

      coreActionSeries.push({ x, y: core });
      coreActionPctSeries.push({ x, y: toPercent(core, active) });
      timeToCoreSeries.push({ x, y: toPercent(activated7, active) });
      repeatSeries.push({ x, y: toPercent(activated14, active) });
    });

    return {
      totalActive,
      totalCoreAction,
      totalActivated7d,
      totalActivated14d,
      coreActionSeries,
      coreActionPctSeries,
      timeToCoreSeries,
      repeatSeries,
    };
  }, [items]);

  if (isFetching) {
    return (
      <CardsGrid>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            animation="wave"
            variant="rect"
            width={'100%'}
            height={280}
            style={{ borderRadius: '12px' }}
          />
        ))}
      </CardsGrid>
    );
  }

  return (
    <CardsGrid>
      <MetricCard
        title="Users doing core action"
        description="Unique users who made at least 1 reservation"
        value={toLocaleString(totalCoreAction)}
        chartData={coreActionSeries}
        chartColor="#6366f1"
      />
      <MetricCard
        title="% users doing core action"
        description="Of active users who made at least 1 reservation"
        value={`${toLocaleString(toPercent(totalCoreAction, totalActive), 1)}%`}
        chartData={coreActionPctSeries}
        chartColor="#16a34a"
        chartSuffix="%"
      />
      <MetricCard
        title="Time to core action"
        description="% of new users who reserved within 7 days"
        value={`${toLocaleString(toPercent(totalActivated7d, totalActive), 1)}%`}
        chartData={timeToCoreSeries}
        chartColor="#a855f7"
        chartSuffix="%"
      />
      <MetricCard
        title="Repeat"
        description="% of users who reserved again within 14 days"
        value={`${toLocaleString(toPercent(totalActivated14d, totalActive), 1)}%`}
        chartData={repeatSeries}
        chartColor="#f97316"
        chartSuffix="%"
      />
    </CardsGrid>
  );
};

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 60px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.lightGrey1};
  border: 1px solid ${({ theme }) => theme.colors.brandLightGrey};
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const CardTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.brandDarkGrey};
`;

const CardDescription = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  line-height: 1.3;
`;

const CardValue = styled.div<{ $muted?: boolean }>`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${({ $muted, theme }) =>
    $muted ? theme.colors.brandLightGrey : theme.colors.brandDarkGrey};
  line-height: 1.1;
  white-space: nowrap;
`;

const ChartArea = styled.div`
  height: 180px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: auto;
`;

const ChartPlaceholderText = styled.span`
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  font-size: 0.75rem;
  font-style: italic;
`;

const TooltipBox = styled.div`
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const TooltipDate = styled.span`
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  font-size: 11px;
`;

const TooltipValue = styled.span`
  font-weight: 600;
`;
